import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  User,
  Mail,
  Phone,
  Building2,
  MapPin,
  ArrowLeft,
  Edit,
  Save,
  Loader2,
  Calendar,
  History,
  MessageSquare,
  Target,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Euro,
  FileText,
  Send,
  UserPlus,
  AlertTriangle,
  Star,
  Sparkles
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';
import ContactHistoryWidget from '../../components/shared/ContactHistoryWidget';

// Status options for prospects
const statusOptions = [
  { value: 'new', label: 'Nouveau', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacté', color: 'bg-amber-100 text-amber-700' },
  { value: 'qualified', label: 'Qualifié', color: 'bg-purple-100 text-purple-700' },
  { value: 'proposal', label: 'Proposition envoyée', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'negotiation', label: 'Négociation', color: 'bg-orange-100 text-orange-700' },
  { value: 'won', label: 'Gagné', color: 'bg-green-100 text-green-700' },
  { value: 'lost', label: 'Perdu', color: 'bg-red-100 text-red-700' }
];

const sourceOptions = [
  { value: 'website', label: 'Site web' },
  { value: 'referral', label: 'Recommandation' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'cold_call', label: 'Prospection téléphonique' },
  { value: 'event', label: 'Événement' },
  { value: 'advertising', label: 'Publicité' },
  { value: 'other', label: 'Autre' }
];

const ProspectDetailPage = () => {
  const { prospectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prospect, setProspect] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [activeTab, setActiveTab] = useState('info');
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (prospectId) {
      fetchProspectData();
    }
  }, [prospectId]);

  const fetchProspectData = async () => {
    try {
      setLoading(true);
      const [prospectRes, historyRes, notesRes] = await Promise.all([
        api.get(`/prospects/${prospectId}`),
        api.get(`/prospects/${prospectId}/contact-history`).catch(() => ({ data: { history: [] } })),
        api.get(`/prospects/${prospectId}/notes`).catch(() => ({ data: { notes: [] } }))
      ]);
      
      const prospectData = {
        ...prospectRes.data,
        contact_history: historyRes.data?.history || prospectRes.data?.contact_history || [],
        internal_notes: notesRes.data?.notes || []
      };
      
      setProspect(prospectData);
      setEditData(prospectData);
    } catch (error) {
      console.error('Error fetching prospect:', error);
      toast.error('Erreur lors du chargement du prospect');
      navigate('/prospects');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/prospects/${prospectId}`, editData);
      setProspect({ ...prospect, ...editData });
      setIsEditing(false);
      toast.success('Prospect mis à jour');
    } catch (error) {
      console.error('Error saving prospect:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToClient = async () => {
    setConverting(true);
    try {
      const res = await api.post(`/prospects/${prospectId}/convert`);
      toast.success('Prospect converti en client');
      navigate(`/clients/${res.data.client_id}`);
    } catch (error) {
      console.error('Error converting prospect:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setConverting(false);
      setShowConvertDialog(false);
    }
  };

  const getStatusBadge = (status) => {
    const option = statusOptions.find(s => s.value === status) || statusOptions[0];
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  if (!prospect) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <p className="text-lg font-medium">Prospect non trouvé</p>
          <Button variant="outline" onClick={() => navigate('/prospects')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux prospects
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/prospects')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Prospects
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-900">{prospect.name}</h1>
                {getStatusBadge(prospect.status)}
              </div>
              <p className="text-slate-500">{prospect.company || 'Particulier'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                {prospect.status !== 'won' && prospect.status !== 'lost' && (
                  <Button 
                    onClick={() => setShowConvertDialog(true)}
                    style={{ backgroundColor: theme.colors?.accent }}
                    className="text-white"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Convertir en client
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => {
                  setIsEditing(false);
                  setEditData(prospect);
                }}>
                  Annuler
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Enregistrer
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column - Main info */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="info" onClick={() => setActiveTab('info')}>
                  <User className="w-4 h-4 mr-2" />
                  Informations
                </TabsTrigger>
                <TabsTrigger value="history" onClick={() => setActiveTab('history')}>
                  <History className="w-4 h-4 mr-2" />
                  Historique
                </TabsTrigger>
                <TabsTrigger value="opportunities" onClick={() => setActiveTab('opportunities')}>
                  <Target className="w-4 h-4 mr-2" />
                  Opportunités
                </TabsTrigger>
              </TabsList>

              {/* Info Tab */}
              <TabsContent value="info" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Coordonnées</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Nom complet</Label>
                        {isEditing ? (
                          <Input
                            value={editData.name || ''}
                            onChange={(e) => setEditData({...editData, name: e.target.value})}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-slate-700">{prospect.name || '-'}</p>
                        )}
                      </div>
                      <div>
                        <Label>Société</Label>
                        {isEditing ? (
                          <Input
                            value={editData.company || ''}
                            onChange={(e) => setEditData({...editData, company: e.target.value})}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-slate-700">{prospect.company || '-'}</p>
                        )}
                      </div>
                      <div>
                        <Label>Email</Label>
                        {isEditing ? (
                          <Input
                            type="email"
                            value={editData.email || ''}
                            onChange={(e) => setEditData({...editData, email: e.target.value})}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-slate-700 flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            {prospect.email || '-'}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        {isEditing ? (
                          <Input
                            value={editData.phone || ''}
                            onChange={(e) => setEditData({...editData, phone: e.target.value})}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-slate-700 flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-400" />
                            {prospect.phone || '-'}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-3">Adresse</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Label>Adresse</Label>
                          {isEditing ? (
                            <Input
                              value={editData.address || ''}
                              onChange={(e) => setEditData({...editData, address: e.target.value})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 text-slate-700">{prospect.address || '-'}</p>
                          )}
                        </div>
                        <div>
                          <Label>Code postal</Label>
                          {isEditing ? (
                            <Input
                              value={editData.postal_code || ''}
                              onChange={(e) => setEditData({...editData, postal_code: e.target.value})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 text-slate-700">{prospect.postal_code || '-'}</p>
                          )}
                        </div>
                        <div>
                          <Label>Ville</Label>
                          {isEditing ? (
                            <Input
                              value={editData.city || ''}
                              onChange={(e) => setEditData({...editData, city: e.target.value})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 text-slate-700">{prospect.city || '-'}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <h4 className="font-medium mb-3">Qualification</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Source</Label>
                          {isEditing ? (
                            <Select
                              value={editData.source || ''}
                              onValueChange={(v) => setEditData({...editData, source: v})}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Sélectionner..." />
                              </SelectTrigger>
                              <SelectContent>
                                {sourceOptions.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="mt-1 text-slate-700">
                              {sourceOptions.find(s => s.value === prospect.source)?.label || prospect.source || '-'}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label>Statut</Label>
                          {isEditing ? (
                            <Select
                              value={editData.status || 'new'}
                              onValueChange={(v) => setEditData({...editData, status: v})}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="mt-1">{getStatusBadge(prospect.status)}</div>
                          )}
                        </div>
                        <div>
                          <Label>Budget estimé</Label>
                          {isEditing ? (
                            <Input
                              type="number"
                              value={editData.estimated_budget || ''}
                              onChange={(e) => setEditData({...editData, estimated_budget: parseFloat(e.target.value) || 0})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 text-slate-700 flex items-center gap-1">
                              <Euro className="w-4 h-4 text-slate-400" />
                              {prospect.estimated_budget ? `${prospect.estimated_budget.toLocaleString()} €` : '-'}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label>Probabilité de conversion</Label>
                          {isEditing ? (
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={editData.conversion_probability || ''}
                              onChange={(e) => setEditData({...editData, conversion_probability: parseInt(e.target.value) || 0})}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 text-slate-700">
                              {prospect.conversion_probability ? `${prospect.conversion_probability}%` : '-'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <Label>Notes</Label>
                      {isEditing ? (
                        <Textarea
                          value={editData.notes || ''}
                          onChange={(e) => setEditData({...editData, notes: e.target.value})}
                          className="mt-1"
                          rows={4}
                          placeholder="Notes sur le prospect..."
                        />
                      ) : (
                        <p className="mt-1 text-slate-700 whitespace-pre-line">
                          {prospect.notes || 'Aucune note'}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* History Tab */}
              <TabsContent value="history" className="mt-4">
                <ContactHistoryWidget
                  entityId={prospectId}
                  entityType="prospect"
                  contactHistory={prospect.contact_history || []}
                  internalNotes={prospect.internal_notes || []}
                  onContactAdded={fetchProspectData}
                  onNoteAdded={fetchProspectData}
                  showInternalNotes={true}
                  showEmailButton={!!prospect.email}
                  clientEmail={prospect.email}
                  clientName={prospect.name}
                  accentColor={theme.colors?.accent}
                />
              </TabsContent>

              {/* Opportunities Tab */}
              <TabsContent value="opportunities" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Opportunités et besoins</CardTitle>
                    <CardDescription>Services et projets potentiels pour ce prospect</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label>Services intéressés</Label>
                        {isEditing ? (
                          <Textarea
                            value={editData.interested_services || ''}
                            onChange={(e) => setEditData({...editData, interested_services: e.target.value})}
                            className="mt-1"
                            rows={3}
                            placeholder="Liste des services qui intéressent le prospect..."
                          />
                        ) : (
                          <p className="mt-1 text-slate-700 whitespace-pre-line">
                            {prospect.interested_services || 'Non renseigné'}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Besoins identifiés</Label>
                        {isEditing ? (
                          <Textarea
                            value={editData.identified_needs || ''}
                            onChange={(e) => setEditData({...editData, identified_needs: e.target.value})}
                            className="mt-1"
                            rows={3}
                            placeholder="Besoins exprimés par le prospect..."
                          />
                        ) : (
                          <p className="mt-1 text-slate-700 whitespace-pre-line">
                            {prospect.identified_needs || 'Non renseigné'}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Prochaine action</Label>
                        {isEditing ? (
                          <div className="grid grid-cols-2 gap-4 mt-1">
                            <Input
                              value={editData.next_action || ''}
                              onChange={(e) => setEditData({...editData, next_action: e.target.value})}
                              placeholder="Action à réaliser..."
                            />
                            <Input
                              type="date"
                              value={editData.next_action_date || ''}
                              onChange={(e) => setEditData({...editData, next_action_date: e.target.value})}
                            />
                          </div>
                        ) : (
                          <p className="mt-1 text-slate-700">
                            {prospect.next_action ? (
                              <>
                                {prospect.next_action}
                                {prospect.next_action_date && (
                                  <span className="text-slate-500 ml-2">
                                    ({new Date(prospect.next_action_date).toLocaleDateString('fr-FR')})
                                  </span>
                                )}
                              </>
                            ) : 'Aucune action planifiée'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right column - Summary */}
          <div className="space-y-6">
            {/* Quick stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Résumé</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Créé le</span>
                  <span className="font-medium">
                    {prospect.created_at ? new Date(prospect.created_at).toLocaleDateString('fr-FR') : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Dernière mise à jour</span>
                  <span className="font-medium">
                    {prospect.updated_at ? new Date(prospect.updated_at).toLocaleDateString('fr-FR') : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Nombre d'échanges</span>
                  <span className="font-medium">{prospect.contact_history?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Budget estimé</span>
                  <span className="font-medium text-green-600">
                    {prospect.estimated_budget ? `${prospect.estimated_budget.toLocaleString()} €` : '-'}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Score */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Score prospect
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="text-4xl font-bold" style={{ color: theme.colors?.accent }}>
                    {prospect.conversion_probability || 0}%
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Probabilité de conversion</p>
                </div>
                <div className="mt-4 w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all" 
                    style={{ 
                      width: `${prospect.conversion_probability || 0}%`,
                      backgroundColor: theme.colors?.accent 
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Quick actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {prospect.email && (
                  <Button variant="outline" className="w-full justify-start" asChild>
                    <a href={`mailto:${prospect.email}`}>
                      <Mail className="w-4 h-4 mr-2" />
                      Envoyer un email
                    </a>
                  </Button>
                )}
                {prospect.phone && (
                  <Button variant="outline" className="w-full justify-start" asChild>
                    <a href={`tel:${prospect.phone}`}>
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler
                    </a>
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => setActiveTab('history')}
                >
                  <History className="w-4 h-4 mr-2" />
                  Ajouter un échange
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Convert to client dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-600" />
              Convertir en client
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Êtes-vous sûr de vouloir convertir <strong>{prospect.name}</strong> en client ?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Un compte client sera créé avec les informations du prospect. Le prospect sera marqué comme "Gagné".
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleConvertToClient} 
              disabled={converting}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {converting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Conversion...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Convertir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default ProspectDetailPage;
